<template>
  <b-container fluid>
    <b-row align-h="center">
      <b-col cols="12" lg="9" class="mb-3">
        <b-card no-body>
          <b-tabs pills card v-model="activeTab">
            <b-tab active>
              <template v-slot:title>Database</template>
              <b-card-text>
                <b-form-group
                  label="Database Backup"
                  description="This will create a backup from the current database! No confirmation needed."
                >
                  <b-button variant="success" @click="createBackup"
                    >Backup now</b-button
                  >
                </b-form-group>
                <b-form @submit.prevent="executeSqlCommand">
                  <b-form-group
                    label="Run SQL"
                    label-for="sql"
                    description="You can put non-query SQLite commands here to execute on the database directly. Use with caution!"
                  >
                    <b-form-textarea
                      id="sql"
                      v-model="sqlCommand"
                      placeholder="Be careful!"
                      rows="3"
                    />
                  </b-form-group>
                  <b-form-group
                    description="You can put command parameters here, they will replace '?' placeholders. One parameter per line."
                  >
                    <b-form-textarea v-model="sqlCommandParameters" rows="1" />
                  </b-form-group>
                  <b-button type="submit" variant="danger">EXECUTE</b-button>
                </b-form>
              </b-card-text>
            </b-tab>
            <b-tab>
              <template v-slot:title>System</template>
              <b-card-text>
                <b-row>
                  <b-col cols="12" xl="4">
                    <h3>WebSocket Server</h3>
                    <div v-if="status">
                      <p>Current client connections: {{ status.clients }}</p>
                    </div>
                    <div v-else>Loading...</div>

                    <h3>Server</h3>
                    <div v-if="status">
                      <p>Total memory: {{ totalMemory }}</p>
                      <p>Free memory: {{ freeMemory }}</p>
                    </div>
                    <div v-else>Loading...</div>

                    <b-button
                      variant="info"
                      size="sm"
                      pill
                      @click="refreshSystemStatus()"
                      >Refresh system status</b-button
                    >
                  </b-col>
                  <b-col cols="12" xl="8" class="mt-3 mt-xl-0">
                    <h3>Client activity</h3>
                    <div>
                      <SimpleChart :chartData="chartData" />
                    </div>
                  </b-col>
                </b-row>
              </b-card-text>
            </b-tab>
            <b-tab>
              <template v-slot:title>Users</template>
              <b-card-text>
                <b-form-group
                  label="Select a user"
                  description="When you click a command button, the appropriate action will be applied to this user."
                >
                  <b-form-select v-model="selectedUser">
                    <option
                      v-for="user in sortedUsers"
                      v-bind:key="user.id"
                      v-bind:value="user"
                      >{{ user.username }}</option
                    >
                  </b-form-select>
                </b-form-group>
                <b-container fluid>
                  <b-row>
                    <b-col cols="12" lg="4">
                      <b-form-group
                        label="Assign new host"
                        description="Hosts can create quizzes."
                      >
                        <b-button @click="confirmGrantHost"
                          >Grant host rights</b-button
                        >
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" lg="4">
                      <b-form-group
                        label="Delete user"
                        description="Deleted users cannot be restored."
                      >
                        <b-button @click="confirmDeleteUser" variant="danger"
                          >Delete user</b-button
                        >
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-container>
              </b-card-text>
            </b-tab>
            <b-tab>
              <template v-slot:title>Quizzes</template>
              <b-card-text>
                <b-form-group
                  label="Select a quiz"
                  description="When you click a command button, the appropriate action will be applied to this quiz."
                >
                  <b-form-select v-model="selectedQuiz">
                    <option
                      v-for="quiz in sortedQuizzes"
                      v-bind:key="quiz.id"
                      v-bind:value="quiz"
                      >{{ quiz.name }}</option
                    >
                  </b-form-select>
                </b-form-group>
                <b-container fluid>
                  <b-row>
                    <b-col cols="12" lg="4">
                      <b-form-group
                        label="Advance status"
                        :description="
                          'Selected quiz status: ' + selectedQuizStatus
                        "
                      >
                        <b-button @click="dialogQuizStatusVisible = true">{{
                          quizStatusAction
                        }}</b-button>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" lg="4">
                      <b-form-group
                        label="Delete quiz"
                        description="Deleted quizzes cannot be restored."
                      >
                        <b-button @click="confirmDeleteQuiz" variant="danger"
                          >Delete quiz</b-button
                        >
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-container>
              </b-card-text>
            </b-tab>
            <b-tab>
              <template v-slot:title>Front End</template>
              <b-form-group
                label="Current version"
                description="Version value set in the backend database."
              >
                <b-badge variant="info">{{
                  $store.state.frontEndVersion
                }}</b-badge>
              </b-form-group>
              <b-form-group
                label="Browser version"
                description="Version active in your current browser."
              >
                <b-badge variant="info">{{ $settings.version }}</b-badge>
              </b-form-group>
              <b-form-group
                label="Set version"
                description="Setting the version here will force a reload on browsers that have cached an old version of this site."
              >
                <b-button @click="updateFrontEndVersion" variant="danger"
                  >Update to current version.</b-button
                >
              </b-form-group>
            </b-tab>
            <b-tab>
              <template v-slot:title>Donations</template>
              <div v-if="donations">
                <div v-for="d in donations" :key="d.index" :label="d.title">
                  <b-form
                    inline
                    @submit.prevent="setDonationValue(d.id, d.value)"
                  >
                    <b-form-group :label="d.title" :description="d.description">
                      <b-form-input
                        v-model="d.value"
                        type="number"
                        class="mr-2"
                        step="0.01"
                      />
                      <b-button type="submit">Set</b-button>
                    </b-form-group>
                  </b-form>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
    </b-row>

    <DialogInfo v-model="infoVisible">{{ infoMessage }}</DialogInfo>
    <DialogError v-model="errorVisible">{{ errorMessage }}</DialogError>
    <DialogYesNo v-model="confirmationVisible" @ok="actionConfirmed">{{
      confirmationMessage
    }}</DialogYesNo>
    <DialogQuizStatus
      v-bind:quiz="selectedQuiz"
      v-model="dialogQuizStatusVisible"
      @ok="changeQuizStatus"
    />
  </b-container>
</template>

<script>
import { Units } from "@/assets/convenience.js";
import DialogQuizStatus from "@/components/host/DialogQuizStatus";
import DialogInfo from "@/components/DialogInfo";
import DialogError from "@/components/DialogError";
import DialogYesNo from "@/components/DialogYesNo";
import explanations from "@/assets/explanations.js";
import SimpleChart from "@/components/utils/SimpleChart";

export default {
  name: "AdminDashboardHome",
  components: {
    DialogError,
    DialogInfo,
    DialogYesNo,
    DialogQuizStatus,
    SimpleChart
  },
  data() {
    return {
      allUsers: {},
      selectedUser: {},
      allQuizzes: {},
      selectedQuiz: {},
      errorVisible: false,
      errorMessage: "",
      infoVisible: false,
      infoMessage: "",
      activeTab: 0,
      sqlCommand: "",
      sqlCommandParameters: "",
      confirmationMessage: "",
      confirmationVisible: false,
      actionToConfirm: null,
      dialogQuizStatusVisible: false,
      status: null,
      chartData: null
    };
  },
  created() {
    this.$topquiz.checkSession(() => {
      this.refreshSystemStatus();

      this.$topquiz.sendCommand("admin-getAllUsers", this.$topquiz.SessionKey);

      this.$topquiz.sendCommand(
        "admin-getAllQuizzes",
        this.$topquiz.SessionKey
      );
    });

    this.$topquiz.on(this, "admin-status_success", params => {
      this.status = params[0];
    });

    this.$topquiz.on(this, "admin-clientStatistics_success", params => {
      var stats = params[0];
      var data = [];
      stats.forEach(item => {
        var dataItem = {
          time: item.time / 1000,
          value: item.total
        };
        data.push(dataItem);
      });
      this.chartData = data;
    });

    this.$topquiz.on(this, "admin-getAllUsers_success", params => {
      var userList = params[0];
      userList.forEach(user => {
        this.$set(this.allUsers, user.id, user);
      });
      if (userList.length > 0) {
        this.selectedUser = userList[0];
      }
    });

    this.$topquiz.on(this, "admin-getAllQuizzes_success", params => {
      var quizList = params[0];
      quizList.forEach(quiz => {
        this.$set(this.allQuizzes, quiz.id, quiz);
      });
      if (quizList.length > 0) {
        this.selectedQuiz = quizList[0];
      }
    });

    this.$topquiz.on(this, "admin-DANGER-run-sql-command_success", params => {
      this.infoMessage =
        "Your command was executed successfully and has affected " +
        params[0] +
        " rows.";
      this.infoVisible = true;
    });

    this.$topquiz.on(this, "admin-createBackup_success", () => {
      this.infoMessage = "Backup created successfully!";
      this.infoVisible = true;
    });

    this.$topquiz.on(this, "admin-makeHost_success", () => {
      this.infoMessage = this.selectedUser.username + " is now a host!";
      this.infoVisible = true;
    });

    this.$topquiz.on(this, "admin-deleteUser_success", () => {
      this.infoMessage = this.selectedUser.username + " has been deleted!";
      this.infoVisible = true;

      this.$delete(this.allUsers, this.selectedUser.id);
      this.selectedUser = {};
    });

    this.$topquiz.on(this, "admin-DANGER-run-sql-command_error", params => {
      this.errorMessage = params[0];
      this.errorVisible = true;
    });

    this.$topquiz.on(this, "quizStatusChanged", params => {
      var quiz = params[2];
      this.$set(this.allQuizzes, quiz.id, quiz);
      if (this.selectedQuiz.id == quiz.id) {
        this.selectedQuiz = quiz;
      }
    });

    this.$topquiz.on(this, "admin-deleteQuiz_success", params => {
      var quizID = params[0];
      this.$delete(this.allQuizzes, quizID);
      this.selectedQuiz = {};
    });

    this.$store.dispatch("donations/loadDonations", this);
  },
  destroyed() {
    this.$topquiz.unsubscribe(this);
  },
  computed: {
    totalMemory() {
      return Units.formatBytes(this.status.totalmem);
    },
    freeMemory() {
      return Units.formatBytes(this.status.freemem);
    },
    sortedUsers() {
      var sortedUsers = this.$_.sortBy(this.allUsers, [
        user => user.username.toLowerCase()
      ]);
      return sortedUsers;
    },
    sortedQuizzes() {
      var sortedQuizzes = this.$_.sortBy(this.allQuizzes, [
        quiz => quiz.name.toLowerCase()
      ]);
      return sortedQuizzes;
    },
    donations() {
      return this.$store.state.donations.donations;
    },
    selectedQuizStatus() {
      if (!this.selectedQuiz || !this.selectedQuiz.status) {
        return "nothing";
      }

      return explanations.quiz_status_names[this.selectedQuiz.status];
    },
    quizStatusAction() {
      if (!this.selectedQuiz || !this.selectedQuiz.status) {
        return "Do nothing";
      }

      return explanations.quiz_action_next[this.selectedQuiz.status];
    }
  },
  methods: {
    executeSqlCommand() {
      this.$topquiz.sendCommand(
        "admin-DANGER-run-sql-command",
        this.$topquiz.SessionKey,
        this.sqlCommand,
        this.sqlCommandParameters
      );
    },
    confirmDeleteQuiz() {
      this.confirmationMessage =
        "You are about to delete the quiz '" +
        this.selectedQuiz.name +
        "' by " +
        this.selectedQuiz.host +
        ". Continue?";
      this.actionToConfirm = "deleteQuiz";
      this.confirmationVisible = true;
    },
    deleteQuiz() {
      if (!this.selectedQuiz) {
        this.errorMessage = "You must select a quiz first.";
        this.errorVisible = true;
        return;
      }

      this.$topquiz.sendCommand(
        "admin-deleteQuiz",
        this.$topquiz.SessionKey,
        this.selectedQuiz.id
      );
    },
    confirmDeleteUser() {
      this.confirmationMessage =
        "You are about to delete the user '" +
        this.selectedUser.username +
        "'. Continue?";
      this.actionToConfirm = "deleteUser";
      this.confirmationVisible = true;
    },
    deleteUser() {
      if (!this.selectedUser) {
        this.errorMessage = "You must select a user first.";
        this.errorVisible = true;
        return;
      }

      this.$topquiz.sendCommand(
        "admin-deleteUser",
        this.$topquiz.SessionKey,
        this.selectedUser.id
      );
    },
    confirmGrantHost() {
      this.confirmationMessage =
        "You are about to grant host permissions to '" +
        this.selectedUser.username +
        "'. Continue?";
      this.actionToConfirm = "grantHost";
      this.confirmationVisible = true;
    },
    grantHost() {
      if (!this.selectedUser) {
        this.errorMessage = "You must select a user first.";
        this.errorVisible = true;
        return;
      }

      this.$topquiz.sendCommand(
        "admin-makeHost",
        this.$topquiz.SessionKey,
        this.selectedUser.id
      );
    },
    newWindow(count) {
      for (var i = 0; i < count; i++) window.open("../dashboard");
    },
    actionConfirmed() {
      eval("this." + this.actionToConfirm + "()");
    },
    setDonationValue(id, value) {
      this.$topquiz.sendCommand(
        "admin-setDonationAmount",
        this.$topquiz.SessionKey,
        id,
        value
      );
    },
    createBackup() {
      this.$topquiz.sendCommand("admin-createBackup", this.$topquiz.SessionKey);
    },
    changeQuizStatus(_, nextStatus) {
      this.$topquiz.sendCommand(
        "host_changeQuizStatus",
        this.$topquiz.SessionKey,
        this.selectedQuiz.id,
        nextStatus
      );
    },
    refreshSystemStatus() {
      this.$topquiz.sendCommand("admin-status", this.$topquiz.SessionKey);
      this.refreshStatistics();
    },
    refreshStatistics() {
      this.$topquiz.sendCommand(
        "admin-clientStatistics",
        this.$topquiz.SessionKey
      );
    },
    updateFrontEndVersion() {
      this.$topquiz.sendCommand(
        "admin-setFrontendVersion",
        this.$topquiz.SessionKey,
        this.$settings.version
      );
    }
  }
};
</script>

<style scoped>
#chartWrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

#clientChart {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
